import * as angular from "angular"
import { defineDirective, doclib, IGeorefControlPoint, IGeorefPictureInfo, IMapMeasuringService, ImportDocument, IOmniaShell, isArray, models, shell } from "../omnia"
import { EditBoundaryOperation } from "./edit-boundary-op"
import { GcpContext } from "./gcp-context"
import { GcpMap } from "./gcp-map"
import { GcpPoint } from "./gcp-point"
import { SourceRasterMap } from "./source-raster-map"
import { TargetMap } from "./target-map"
import { IBoundaryEditResult, IGeorefArguments, IGeorefLayerUpateModel, IGeorefReprojectedImage, IGeorefReprojectionModel, IGeorefService, IGeorefSourceImage } from "./types"

import IMapTopologyService = omnia.map.IMapTopologyService
import { ol_format, ol_geom } from "@omnia/exports/openlayers"

export class GeorefCtrl extends shell.Dialog {

    static create(): ng.IDirective {

        let def = defineDirective(GeorefCtrl, "app/datalayers/georef/georef.html");

        def.link = (scope, element, attrs, ctrl: GeorefCtrl) => {
            ctrl.link(element);
        };

        return def;

    };


    private rootElement: HTMLElement;

    private georefArguments: IGeorefArguments;

    private targetMap: TargetMap;
    private sourceRaster: SourceRasterMap;

    private context: GcpContext;

    public layerName: string;

    public sourceRasterName: string;
    public sourceRasterLoading: boolean;

    private keyboardHandler: Function;
    private ctrlKey: boolean;

    private editBoundaryOp: EditBoundaryOperation;


    static $inject = ["$scope", "$timeout", "OmniaShell", "OmniaDatalayersGeorefService", "OmniaMapTopologyService", "OmniaMapMeasuringService"];

    constructor($scope: ng.IScope, private $timeout: ng.ITimeoutService, shell: IOmniaShell, private svc: IGeorefService, private topo: IMapTopologyService, private measure: IMapMeasuringService) {
        super($scope, shell);
    }

    $onInit() {
        this.setup();
    }

    $onDestroy() {

        if (this.keyboardHandler) {
            this.keyboardHandler();
            this.keyboardHandler = null;
        }

    }

    link(element) {
        this.rootElement = element[0];
        this.setup();
    }

    protected setup() {

        if (!this.rootElement) return;
        if (!this.svc) return;

        this.georefArguments = this.arguments;

        this.setupKeyboardHandler();
        this.setupMaps();
        this.setupTarget();
        this.setupSource();


    }

    private setupKeyboardHandler() {

        let handler = (event: KeyboardEvent) => {
            this.ctrlKey = event.ctrlKey;
            this.$scope.$applyAsync();
        };

        this.keyboardHandler = () => {
            window.removeEventListener("keydown", handler);
            window.removeEventListener("keyup", handler);
        }

        window.addEventListener("keydown", handler);
        window.addEventListener("keyup", handler);

    }

    private setupSource() {

        let args: IGeorefArguments = this.georefArguments;

        if (args.georefPictureInfo) {
            this.setupExistingPicture(args.georefPictureInfo);
        }
        else if (args.sourceImage) {
            this.setupSourceRaster(args.sourceImage, null);
        }
        else {
            this.$timeout(() => this.selectRaster());
        }


    }

    private setupTarget() {

        let args: IGeorefArguments = this.georefArguments;

        let boundaryWkt = args.field.boundaryWkt;

        let reader = new ol_format.WKT();
        let readerOptions = { dataProjection: "EPSG:3857" };

        let fieldGeometry = reader.readGeometry(boundaryWkt, readerOptions);
        let geometry: ol_geom.Geometry = fieldGeometry;

        if (args.georefPictureInfo && args.georefPictureInfo.BoundaryWkt) {
            geometry = reader.readGeometry(args.georefPictureInfo.BoundaryWkt, readerOptions);
            this.context.targetBoundary = geometry;
        }

        let extent = fieldGeometry.getExtent();

        this.targetMap.setupEditor(fieldGeometry, geometry, extent);

    }

    private updatePointLayers() {

        let points = this.context.getPoints();
        this.targetMap.updatePointsLayer(points);
        this.sourceRaster.updatePointsLayer(points);

    }

    private setupMaps() {

        let context = this.context = new GcpContext(() => this.$scope.$applyAsync());

        this.targetMap = new TargetMap(context, this.shell.getSession().bingKey)
        this.setupMap(this.targetMap, ".map > .olmap-container");

        this.sourceRaster = new SourceRasterMap(context);
        this.setupMap(this.sourceRaster, ".raster > .olmap-container");

    }

    private setupMap(map: GcpMap, targetElementSelector: string) {

        let elt = this.getTargetElement(targetElementSelector);
        map.setupMap(elt);

        this.context.registerMap(map);

    }
    private setupExistingPicture(info: IGeorefPictureInfo) {

        this.svc.getSourceImageInfo(info.SourceDocumentId).then(doc => {

            this.setupSourceRaster(doc, info.ControlPoints);

            let img: IGeorefReprojectedImage = {
                extent: info.Extent,
                imageMimeType: info.ImageMimeType,
                imageUrl: this.svc.getImageUrl(info.Path)
            }

            this.context.reprojectedImage = img;
            this.targetMap.updateReprojectedImage();


        });

    }

    private setupSourceRaster(doc: IGeorefSourceImage, controlPoints: IGeorefControlPoint[]) {

        this.sourceRasterLoading = true;

        if (!(doc.Width && doc.Height)) {

            this.getSourceRasterDimensions(doc).then(r => {

                if (r.Width && r.Height) {
                    this.setupSourceRaster(r, controlPoints)
                }

            });

            return;
        }

        this.sourceRasterLoading = false;

        let url = this.svc.getImageUrl(doc.Path);

        this.sourceRasterName = doc.Description;
        this.sourceRaster.setupEditor(url, doc.Width, doc.Height);

        this.context.setSourceRasterDoc(doc);

        this.layerName = doc.Description;

        if (isArray(controlPoints)) {
            this.restoreControlPoints(controlPoints, doc.Height);
        }
        else {
            this.restoreState(this.georefArguments.field.fieldId, doc);
        }

        this.$timeout(100).then(() => {
            this.targetMap.updateSize();
            this.sourceRaster.updateSize();
        });

    }

    private restoreState(fieldId: number, doc: IGeorefSourceImage) {

        let state = this.svc.getState(fieldId, doc.Id);

        if (state) {
            this.restoreControlPoints(state.ControlPoints, doc.Height);
        }

    }

    private restoreControlPoints(points: IGeorefControlPoint[], imageHeight: number) {

        points.forEach(cp => {

            let p = this.context.addPoint([cp.Map.X, cp.Map.Y], false);
            this.context.completePoint(p, [cp.Raster.X, imageHeight ? imageHeight - cp.Raster.Y : cp.Raster.Y]);

        });

        this.updatePointLayers();

    }

    getSourceRasterDimensions(doc: IGeorefSourceImage): ng.IPromise<IGeorefSourceImage> {

        return this.svc.getImageElementFromImportDocument(doc).then(img => {

            doc = angular.copy(doc)

            doc.Width = img.width;
            doc.Height = img.height;

            return doc;

        })

    }

    private getTargetElement(selector: string): HTMLElement {
        let elt = <HTMLElement>this.rootElement.querySelector(selector);
        return elt;
    }

    private centerPoint(p: GcpPoint) {
        this.targetMap.centerFeature(p.map);
        this.sourceRaster.centerFeature(p.raster);
    }

    private deletePoint(p: GcpPoint) {
        this.context.deletePoint(p);
        this.updatePointLayers();
    }

    canReproject(): boolean {
        return this.context && this.context.sourceRasterDoc && (this.context.getPoints().length >= 4);
    }


    hasSourceRaster(): boolean {
        return this.context && (!!this.context.sourceRasterDoc);
    }

    hasReprojectedImage(): boolean {
        return this.context && (!!this.context.reprojectedImage);
    }

    get reprojectedImageOpacity(): number {
        return this.targetMap.reprojectedImageOpacity;
    }

    set reprojectedImageOpacity(value: number) {
        this.targetMap.reprojectedImageOpacity = value;
    }

    get showFieldBoundary(): boolean {
        return this.targetMap && this.targetMap.showFieldBoundary;
    }

    set showFieldBoundary(value: boolean) {
        this.targetMap.showFieldBoundary = value;
    }

    get addPointMode(): boolean {
        if (this.isEditingBoundary()) return false;
        if (this.context.addPointMode) return true;
        if (this.ctrlKey) return true;
        return false;
    }

    canEditBoundary(): boolean {
        return this.canAddNewPoint() && (!this.isEditingBoundary());
    }

    startBoundaryEdit() {

        if (!this.editBoundaryOp) {
            let stop = (result: IBoundaryEditResult) => this.stopBoundaryEdit(result);
            let apply = () => this.$scope.$applyAsync();
            this.editBoundaryOp = EditBoundaryOperation.start(this.context, this.targetMap, this.topo, this.measure, stop, apply, this.$timeout);
        }

    }

    private stopBoundaryEdit(result: IBoundaryEditResult) {

        if (result) {

            this.context.targetBoundary = result.boundary;
            this.targetMap.setBoundary(result.boundary);

            if (this.hasReprojectedImage()) {
                this.reproject();
            }

        }

        this.editBoundaryOp = null;


    }

    isEditingBoundary(): boolean {
        return !!this.editBoundaryOp;
    }

    get editBoundaryOperation(): EditBoundaryOperation {
        return this.editBoundaryOp;
    }

    canAddNewPoint(): boolean {

        if (this.context && this.hasSourceRaster()) {
            let point = this.context.currentPoint;
            return ((!point) || point.isComplete());
        }

    }

    canChangeSourceRaster(): boolean {
        return this.hasSourceRaster() && this.canAddNewPoint() && (!this.isEditingBoundary());
    }

    startAddNewPoint() {
        this.context.addPointMode = !this.context.addPointMode;
    }

    selectRaster() {

        const args: doclib.IDocumentLibraryArguments = {
            selection: true,
            limitToPictures: true,
            title: "Select the picture to georeference",
        };

        this.shell.showComponentAsDialog(doclib.ReactDocumentLibrary, args, { size: "xlarge" }).then(r => {

            if (isArray(r)) {

                let doc: ImportDocument = r[0];
                let img = this.svc.getSourceImageFromImportdoc(doc);

                this.setupSourceRaster(img, null);

            }

        });

    }

    private getReprojectionModel(): IGeorefReprojectionModel {

        let rasterUrl = this.svc.getImageUrl(this.context.sourceRasterDoc.Path);

        let rasterHeight = this.context.sourceRasterSize[1];

        let boundary = this.georefArguments.field.boundaryWkt;

        if (this.context.targetBoundary) {
            boundary = (new ol_format.WKT()).writeGeometry(this.context.targetBoundary);
        }

        let model: IGeorefReprojectionModel = {

            SourceUrl: rasterUrl,
            SourceDocumentId: this.context.sourceRasterDoc.Id,

            FieldBoundaryWkt: this.georefArguments.field.boundaryWkt,
            BoundaryWkt: boundary,

            MaxImageWidthOrHeight: 2000,

            ControlPoints: this.context.getPoints().map(p => {
                return {
                    Map: p.getMapCoordXY(),
                    Raster: p.getRasterCoordXY(rasterHeight)
                }
            })
        };

        return model;

    }

    reproject() {

        let model = this.getReprojectionModel();

        if (this.shell.getSession().debugMode) {
            this.svc.saveState(this.georefArguments.field.fieldId, this.context.sourceRasterDoc.Id, model);
        }

        this.svc.reprojectImage(model).then(r => {

            this.context.reprojectedImage = r;
            this.targetMap.updateReprojectedImage();

        });

    }

    canDeleteLayer(): boolean {
        if (this.isEditingBoundary()) return false;
        return !!this.georefArguments.layerId;
    }

    deleteLayer(): void {

        let content = "<p>Click 'Delete' to delete this layer and the georeferenced picture.</p><p>Please note that this operation is irreversible.</p>";

        shell.showDeleteCancel(this.shell, content, "Delete this layer").then(r => {
            if (r) {
                this.svc.deleteLayer(this.georefArguments.layerId)
                    .then(r => this.close(-1))
            }
        });

    }

    canCancel(): boolean {
        return !this.isEditingBoundary();
    }

    canCommit(): boolean {

        if (this.isEditingBoundary()) return false;
        if (!this.canReproject()) return false;
        if (this.form && this.form.$invalid) return false;
        if (!this.hasReprojectedImage()) return false;

        return true;

    }

    commit() {

        let model: IGeorefLayerUpateModel = {
            LayerId: this.georefArguments.layerId,
            FieldId: this.georefArguments.field.fieldId,
            Name: this.layerName,
            Reprojection: this.getReprojectionModel(),
        }

        this.svc.saveLayer(model).then(r => this.close(r));

    }
}
